import React from 'react';
import { CheckCircle2, AlertTriangle, XCircle, Brain, Layers, Image as ImageIcon, Database } from 'lucide-react';
import { Badge } from '../../../../../components/ui/Badge';
import { IssueRow, MODULE_CONFIG } from '../shared';
import type { UnifiedQuestionRow } from '../hooks/useGovernanceData';
interface QuestionDetailPanelProps {
  row: UnifiedQuestionRow;
}
export function QuestionDetailPanel({
  row
}: QuestionDetailPanelProps) {
  const modConfig = MODULE_CONFIG[row.moduleCode] || {
    bgClass: 'bg-gray-500',
    textClass: 'text-gray-700'
  };
  return <div className="px-4 py-4 bg-gray-50 border-t border-gray-100" data-id="element-3150">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-3151">
        {/* Left Column: Pipeline Issues */}
        <div className="space-y-4" data-id="element-3152">
          <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-1.5" data-id="element-3153">
            <AlertTriangle className="w-3.5 h-3.5" data-id="element-3154" /> Pipeline Issues
          </h4>

          {row.pipelineIssues.length === 0 ? <div className="p-4 bg-white rounded-lg border border-gray-200 text-center" data-id="element-3155">
              <CheckCircle2 className="w-6 h-6 text-emerald-400 mx-auto mb-1" data-id="element-3156" />
              <p className="text-sm text-gray-600" data-id="element-3157">
                Geen pipeline issues gevonden.
              </p>
            </div> : <div className="space-y-2" data-id="element-3158">
              {row.pipelineIssues.map((issue, idx) => <IssueRow key={idx} issue={issue} data-id="element-3159" />)}
            </div>}
        </div>

        {/* Right Column: Metadata & Quality */}
        <div className="space-y-6" data-id="element-3160">
          {/* Quality & Metadata */}
          <div data-id="element-3161">
            <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-1.5 mb-3" data-id="element-3162">
              <Brain className="w-3.5 h-3.5" data-id="element-3163" /> Kwaliteit & Metadata
            </h4>

            <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-4" data-id="element-3164">
              <div className="grid grid-cols-2 gap-4" data-id="element-3165">
                <div data-id="element-3166">
                  <p className="text-xs text-gray-500 mb-1" data-id="element-3167">Cognitief Niveau</p>
                  <p className="text-sm font-medium text-gray-900" data-id="element-3168">
                    Niveau {row.cognitiveLevel}
                  </p>
                </div>
                <div data-id="element-3169">
                  <p className="text-xs text-gray-500 mb-1" data-id="element-3170">Module & Tier</p>
                  <div className="flex items-center gap-1.5" data-id="element-3171">
                    <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${modConfig.bgClass} text-white`} data-id="element-3172">
                      {row.moduleCode}
                    </span>
                    <span className="text-sm font-medium text-gray-900" data-id="element-3173">
                      Tier {row.tier}
                    </span>
                  </div>
                </div>
                <div className="col-span-2" data-id="element-3174">
                  <p className="text-xs text-gray-500 mb-1" data-id="element-3175">Skill</p>
                  <p className="text-sm font-medium text-gray-900 font-mono bg-gray-50 px-2 py-1 rounded inline-block" data-id="element-3176">
                    {row.skill}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Seed Status */}
          <div data-id="element-3177">
            <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-1.5 mb-3" data-id="element-3178">
              <Database className="w-3.5 h-3.5" data-id="element-3179" /> Seed Status
            </h4>

            {row.seedWarnings.length === 0 ? <div className="p-3 bg-white rounded-lg border border-gray-200 flex items-center gap-2" data-id="element-3180">
                <CheckCircle2 className="w-4 h-4 text-emerald-500" data-id="element-3181" />
                <span className="text-sm text-gray-600" data-id="element-3182">
                  In sync met taxonomy
                </span>
              </div> : <div className="space-y-2" data-id="element-3183">
                {row.seedWarnings.map((warn, i) => <div key={i} className="p-2.5 bg-amber-50 rounded-lg border border-amber-200 text-xs text-amber-800 font-mono" data-id="element-3184">
                    {warn}
                  </div>)}
              </div>}
          </div>

          {/* Visual Status */}
          <div data-id="element-3185">
            <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-1.5 mb-3" data-id="element-3186">
              <ImageIcon className="w-3.5 h-3.5" data-id="element-3187" /> Visuele Hulpmiddelen
            </h4>

            <div className="p-3 bg-white rounded-lg border border-gray-200" data-id="element-3188">
              {row.hasVisualProblems ? <div className="flex items-start gap-2 text-red-700" data-id="element-3189">
                  <XCircle className="w-4 h-4 mt-0.5 flex-shrink-0" data-id="element-3190" />
                  <span className="text-sm" data-id="element-3191">
                    Er zijn problemen met de visuals (zie pipeline issues).
                  </span>
                </div> : <div className="flex items-start gap-2 text-emerald-700" data-id="element-3192">
                  <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" data-id="element-3193" />
                  <span className="text-sm" data-id="element-3194">
                    Visuals zijn geldig en correct geconfigureerd.
                  </span>
                </div>}
            </div>
          </div>
        </div>
      </div>
    </div>;
}